import { Injectable, Logger } from '@nestjs/common';
import { execFile } from 'node:child_process';
import { createHash, randomUUID } from 'node:crypto';
import { gzipSync, gunzipSync, zstdDecompressSync } from 'node:zlib';
import { accessSync, constants as fsConstants, existsSync, readdirSync } from 'node:fs';
import { mkdir, readFile, readdir, rm, stat, writeFile } from 'node:fs/promises';
import { tmpdir, userInfo } from 'node:os';
import { join } from 'node:path';
import { PrismaService, S3CompatibleAdapter, StorageAdapter } from '@cart/api-data-access';
import {
  backupEnvPrefix,
  backupRootPrefix,
  baseBackupPrefix,
  basePrefix,
  dumpPrefix,
  readBackupStorageEnv,
  segmentNameFromFile,
  walFileName,
  walPrefix,
} from './backup-storage.constants';
import { databaseUrl } from './database-url';

/**
 * Windows path -> forward slashes.
 *
 * Both `tar` (bsdtar, in System32) and `pg_basebackup -D` mishandle a native
 * Windows path here. Kept as a named helper rather than open-coded, because a
 * stray escape sequence eats the separators — a real failure hit here, where a
 * staging path collapsed and pg_basebackup reported
 * `could not create directory "C:pitrase"`.
 */
function toPosixPath(value: string): string {
  return value.split(String.fromCharCode(92)).join('/');
}

/** Windows installs pg_dump here; probed in version-descending order. */
const WINDOWS_PG_ROOTS = ['C:/Program Files/PostgreSQL', 'C:/Program Files (x86)/PostgreSQL'];

/**
 * Where PostgreSQL binaries live on Linux when they are not on PATH.
 *
 * Debian/Ubuntu keep them per-version outside PATH; RHEL uses /usr/pgsql-NN.
 */
const POSIX_PG_ROOTS = ['/usr/lib/postgresql', '/usr/pgsql'];

/**
 * Find an executable, or return null if it genuinely is not there.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * The previous probe ended `return process.platform === 'win32' ? null : 'pg_dump'`
 * — on Linux it reported success unconditionally, without looking. That made
 * `isAvailable()` always true on the server, so `backend()` was never 'none'
 * and the whole subsystem behaved as if it could take backups on a host where
 * pg_dump may not be installed at all. A capability check that cannot fail is
 * not a capability check; it just moves the error to somewhere less legible
 * (an ENOENT from execFile, at 02:00, inside a cron).
 */
function resolveBinary(name: string, explicit: string | undefined): string | null {
  if (explicit && existsSync(explicit)) return explicit;

  if (process.platform === 'win32') {
    for (const root of WINDOWS_PG_ROOTS) {
      if (!existsSync(root)) continue;
      const versions = readdirSync(root)
        .filter((entry) => /^\d+$/.test(entry))
        .sort((a, b) => Number(b) - Number(a));
      for (const version of versions) {
        const candidate = join(root, version, 'bin', `${name}.exe`);
        if (existsSync(candidate)) return candidate;
      }
    }
    return null;
  }

  for (const dir of (process.env.PATH ?? '').split(':').filter(Boolean)) {
    const candidate = join(dir, name);
    if (existsSync(candidate)) return candidate;
  }

  for (const root of POSIX_PG_ROOTS) {
    if (!existsSync(root)) continue;
    const versions = readdirSync(root)
      .filter((entry) => /^\d+$/.test(entry))
      .sort((a, b) => Number(b) - Number(a));
    for (const version of versions) {
      const candidate = join(root, version, 'bin', name);
      if (existsSync(candidate)) return candidate;
    }
  }

  return null;
}

export interface DumpResult {
  /** The R2 key the dump was uploaded to. */
  storageKey: string;
  /** Compressed size — what the budget actually pays for. */
  sizeBytes: number;
  /** Uncompressed size, kept so the ratio stays visible rather than folklore. */
  rawBytes: number;
  checksum: string;
  durationMs: number;
}

/**
 * `pg_dump` backups that upload to R2, so the subsystem runs on any machine.
 *
 * ── WHERE THE BYTES GO ───────────────────────────────────────────────────────
 *
 * Straight to R2, under the environment prefix — a developer machine backs up
 * to the same bucket the server does and lands under `test/`:
 *
 *   test/database/backups/databases/<timestamp>_<database>.sql
 *   production/database/backups/databases/<timestamp>_<database>.sql
 *
 * Local disk is only a staging area: `pg_dump` must write a file before it can
 * be uploaded, and that file is deleted once the upload is confirmed. It lives
 * in the OS temp directory rather than the repo, so a failed run cannot leave a
 * 95 MB dump of the database sitting inside a git working tree.
 *
 * ── WHAT THIS IS, AND WHAT IT IS NOT ─────────────────────────────────────────
 *
 * A **logical** backup. That distinction is not pedantry — it is the whole
 * difference in what can be recovered:
 *
 *   pg_dump          -> restore to THE INSTANT THE DUMP RAN. Nothing between dumps.
 *   pgBackRest + WAL -> restore to ANY SECOND inside the window.
 *
 * So a machine running this backend has a recovery window made of points, not a
 * continuous band, and the timeline shows it that way. Do not read a green dev
 * screen as evidence that production PITR works — that is exactly the
 * "successful backup, failed recovery" confusion this subsystem exists to end.
 *
 * ── IT SPENDS THE SAME BUDGET ────────────────────────────────────────────────
 *
 * Dev dumps are charged against the same 1 GB ceiling as production ones,
 * because they are in the same bucket. A working dev database dumps to roughly
 * 95 MB, so three retained dev dumps is nearly a third of the budget. The quota
 * admission check treats them identically, which is correct — they occupy real
 * bytes — but it means retention on the dev policy is a real decision, not a
 * formality.
 */
@Injectable()
export class LocalBackupService {
  private readonly logger = new Logger(LocalBackupService.name);

  /** Resolved binary paths. `null` means "looked for it, not there". */
  private readonly resolved: Record<string, string | null | undefined> = {};

  constructor(private readonly prisma: PrismaService) {}

  environment(): string {
    return backupEnvPrefix();
  }

  /**
   * The R2 destination.
   *
   * `protected` rather than private so a spec can substitute a fake without a
   * DI token — the destination is the one collaborator here that talks to a
   * third-party network, and every test would otherwise reach a real bucket.
   * Same shape as MediaBackupService.destination() for the same reason.
   */
  protected destination(): StorageAdapter | null {
    // Dedicated BACKUP_* credentials if present, otherwise the platform's
    // existing PLATFORM_R2_* ones — media and backups then share a bucket and
    // are separated by key prefix, which is how this deployment is set up.
    const env = readBackupStorageEnv();
    if (!env) return null;

    return new S3CompatibleAdapter({
      bucket: env.bucket,
      region: env.region,
      endpoint: env.endpoint,
      accessKeyId: env.accessKeyId,
      secretAccessKey: env.secretAccessKey,
      forcePathStyle: true,
    });
  }

  /**
   * True when backups are using the media credentials.
   *
   * Surfaced so the admin screen can say so rather than implying an isolation
   * that is not there: one key means the key serving media can also delete
   * backup history, and R2 object lock becomes the only remaining defence.
   */
  sharesMediaCredentials(): boolean {
    return readBackupStorageEnv()?.sharedWithMedia ?? false;
  }

  /**
   * Locate `pg_dump`.
   *
   * PATH first, then the versioned Windows install directories, newest first —
   * pg_dump can dump from an older server but not a newer one, so the highest
   * version present is the choice most likely to work.
   */
  pgDumpPath(): string | null {
    // Cached: isAvailable() is called on every scheduler tick and every admin
    // request, and each miss is a handful of stat() calls.
    if (this.resolved['pg_dump'] === undefined) {
      this.resolved['pg_dump'] = resolveBinary('pg_dump', process.env.PG_DUMP_PATH);
    }
    return this.resolved['pg_dump'];
  }

  /** Both halves are required: something to run, and somewhere to put it. */
  isAvailable(): boolean {
    return this.pgDumpPath() !== null && this.destination() !== null;
  }

  /** Why it is unavailable, in words the admin screen can show. */
  unavailableReason(): string | null {
    if (!this.pgDumpPath()) return 'pg_dump was not found — set PG_DUMP_PATH to its full path.';
    if (!this.destination()) {
      return (
        'No backup destination configured. Set BACKUP_BUCKET / BACKUP_ENDPOINT / ' +
        'BACKUP_ACCESS_KEY_ID / BACKUP_SECRET_ACCESS_KEY, or configure PLATFORM_R2_* ' +
        'to share the media bucket.'
      );
    }
    return null;
  }

  /**
   * Parse DATABASE_URL into pg_dump arguments.
   *
   * The password is deliberately NOT an argument — it goes into the child's
   * environment as PGPASSWORD, because anything on a command line is visible in
   * the process list to every user on the machine.
   */
  private connection(): { args: string[]; password: string | null; database: string } | null {
    const raw = process.env.DATABASE_URL;
    if (!raw) return null;

    try {
      const url = new URL(raw);
      const database = url.pathname.replace(/^\//, '') || 'postgres';
      return {
        args: [
          '--host', url.hostname,
          '--port', url.port || '5432',
          '--username', decodeURIComponent(url.username || 'postgres'),
          '--dbname', database,
        ],
        password: url.password ? decodeURIComponent(url.password) : null,
        database,
      };
    } catch {
      return null;
    }
  }

  /** `20260829_171233` — sorts chronologically as a plain string. */
  private timestamp(at: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}` +
      `_${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`
    );
  }

  /**
   * Dump, upload, delete the staging file.
   *
   * Plain SQL rather than the custom format: readable, diffable, and restorable
   * with nothing but `psql`, which is what you want from the copy you reach for
   * while debugging. Production's physical backups use pgBackRest, where the
   * trade-offs are different.
   */
  async runDump(): Promise<DumpResult> {
    const binary = this.pgDumpPath();
    const destination = this.destination();
    if (!binary || !destination) throw new Error(this.unavailableReason() ?? 'backup unavailable');

    const connection = this.connection();
    if (!connection) throw new Error('DATABASE_URL is missing or unparseable');

    // Staged in the OS temp directory, never in the repo: a failed run must not
    // leave a full copy of the database inside a git working tree.
    const staging = join(tmpdir(), 'forgestack-backup');
    await mkdir(staging, { recursive: true });

    const filename = `${this.timestamp()}_${connection.database}.sql`;
    const stagedPath = join(staging, filename);
    const started = Date.now();

    try {
      await new Promise<void>((resolvePromise, reject) => {
        execFile(
          binary,
          [
            ...connection.args,
            '--format', 'plain',
            // Restoring over an existing database is the common case, so the
            // dump has to be able to replace what is there.
            '--clean',
            '--if-exists',
            '--no-owner',
            '--no-privileges',
            '--file', stagedPath,
          ],
          {
            env: {
              ...process.env,
              ...(connection.password ? { PGPASSWORD: connection.password } : {}),
            },
            shell: false,
            maxBuffer: 32 * 1024 * 1024,
            timeout: 30 * 60 * 1000,
          },
          (err, _stdout, stderr) => {
            if (err) {
              reject(new Error(String(stderr || err.message).slice(0, 2000)));
              return;
            }
            resolvePromise();
          }
        );
      });

      const raw = await readFile(stagedPath);

      // Gzipped before upload. Measured on this database: 98 MB of plain SQL
      // becomes 10.3 MB — 9.5:1 for about a second of CPU. Uploading it
      // uncompressed was costing ~10% of the entire 1 GB budget per dump, which
      // is the difference between keeping a fortnight of backups and keeping
      // two.
      const buffer = gzipSync(raw, { level: 6 });
      // Checksum over the ORIGINAL bytes, so it identifies the dump rather than
      // one particular compression of it.
      const checksum = createHash('sha256').update(raw).digest('hex');
      const storageKey = `${dumpPrefix()}/${filename}.gz`;

      await destination.save(buffer, storageKey, 'application/gzip');

      return {
        storageKey,
        sizeBytes: buffer.byteLength,
        rawBytes: raw.byteLength,
        checksum,
        durationMs: Date.now() - started,
      };
    } finally {
      // Always, including on failure. The staging file is a full copy of the
      // database and there is no reason for it to outlive the upload.
      await rm(stagedPath, { force: true }).catch(() => undefined);
    }
  }

  /**
   * Take a physical base backup and upload it, compressed.
   *
   * ── COMPRESSION IS WHAT MAKES PITR AFFORDABLE HERE ──────────────────────────
   *
   * Measured on this database, not estimated:
   *
   *   uncompressed base backup   435 MB
   *   server-zstd:9              45 MB    ← ~10:1
   *
   * Against a 1 GB shared budget the uncompressed form is simply not
   * purchasable — one backup would be 42% of everything, before a single WAL
   * segment. Compressed, two base backups cost 90 MB and the window becomes a
   * real option. `server-` matters too: the compression happens on the server,
   * so the 435 MB never crosses the wire.
   *
   * zstd rather than gzip because Node 22 can decompress it natively
   * (`zlib.zstdDecompressSync`), so the restore path needs no external tool
   * beyond `tar`, which Windows ships in System32.
   */
  async runBaseBackup(): Promise<{
    prefix: string;
    sizeBytes: number;
    rawBytes: number;
    checksum: string;
    durationMs: number;
    files: string[];
  }> {
    const binary = this.pgBaseBackupPath();
    const destination = this.destination();
    if (!binary) throw new Error('pg_basebackup not found. Set PG_BIN_DIR or PG_DUMP_PATH.');
    if (!destination) throw new Error(this.unavailableReason() ?? 'no backup destination');

    const url = databaseUrl();
    const password = url.password ? decodeURIComponent(url.password) : null;

    const timestamp = this.timestamp();
    // The timestamp only resolves to the second, so two runs starting in the
    // same second shared this directory — and pg_basebackup CLEARS its target
    // before writing. Three duplicate cron registrations made that happen for
    // real: three runs at 01:00:04 destroyed each other's files and all three
    // failed. The cron duplication is fixed, but a staging path that is unique
    // by construction costs nothing and removes the class of failure.
    const staging = join(tmpdir(), `forgestack-base-${timestamp}-${randomUUID().slice(0, 8)}`);
    await mkdir(staging, { recursive: true });

    const started = Date.now();

    try {
      await new Promise<void>((resolvePromise, reject) => {
        execFile(
          binary,
          [
            '-U', decodeURIComponent(url.username || 'postgres'),
            '-h', url.hostname,
            '-p', url.port || '5432',
            // Forward slashes: pg_basebackup on Windows mangles a path whose
            // backslashes survive the shell-free argv, and this is the one
            // argument that is a filesystem path.
            '-D', staging.split('\\').join('/'),
            // tar is required for compression; -Xs streams WAL during the copy
            // so the backup is self-consistent even if it takes minutes.
            '-Ft',
            '-Xs',
            '--compress=server-zstd:9',
          ],
          {
            env: { ...process.env, ...(password ? { PGPASSWORD: password } : {}) },
            shell: false,
            maxBuffer: 32 * 1024 * 1024,
            timeout: 60 * 60 * 1000,
          },
          (err, _stdout, stderr) => {
            if (err) reject(new Error(String(stderr || err.message).slice(0, 2000)));
            else resolvePromise();
          }
        );
      });

      const prefix = baseBackupPrefix(timestamp);
      const produced = await readdir(staging);

      let sizeBytes = 0;
      const hash = createHash('sha256');
      const files: string[] = [];

      for (const name of produced) {
        const buffer = await readFile(join(staging, name));
        // pg_wal.tar comes out uncompressed; gzip it rather than shipping 16 MB
        // of mostly-zeroes into a 1 GB budget.
        const shouldGzip = name.endsWith('.tar');
        const payload = shouldGzip ? gzipSync(buffer, { level: 9 }) : buffer;
        const key = `${prefix}/${name}${shouldGzip ? '.gz' : ''}`;

        await destination.save(payload, key, 'application/octet-stream');

        sizeBytes += payload.byteLength;
        hash.update(payload);
        files.push(key);
      }

      const rawBytes = (
        await Promise.all(produced.map((n) => stat(join(staging, n)).then((i) => i.size)))
      ).reduce((a, b) => a + b, 0);

      return {
        prefix,
        sizeBytes,
        rawBytes,
        checksum: hash.digest('hex'),
        durationMs: Date.now() - started,
        files,
      };
    } finally {
      await rm(staging, { recursive: true, force: true }).catch(() => undefined);
    }
  }

  /** pg_basebackup, beside whichever pg_dump was found. */
  pgBaseBackupPath(): string | null {
    if (this.resolved['pg_basebackup'] === undefined) {
      this.resolved['pg_basebackup'] = resolveBinary('pg_basebackup', undefined);
    }
    return this.resolved['pg_basebackup'];
  }

  /**
   * Upload newly archived WAL segments.
   *
   * Reads Postgres's archive directory (BACKUP_WAL_SOURCE) and puts each new
   * segment at `<env>/database/backups/wal/<segment>.walfomat`.
   *
   * Returns zero when archiving is off, which is the honest answer — the
   * alternative would be a timeline claiming continuous recoverability that no
   * data supports.
   */
  /**
   * Why WAL archiving is not running, or null when it is.
   *
   * `collectWal` returned 0 for both "archiving is off" and "nothing new to
   * archive", every two minutes, saying nothing. On the server that silence
   * lasted until someone noticed the restore screen reporting 0 segments and a
   * 51-minute data-loss window. The two cases need different answers, so they
   * get different answers.
   */
  walArchivingBlockedReason(): string | null {
    const source = process.env.BACKUP_WAL_SOURCE;

    if (!source) {
      return (
        'BACKUP_WAL_SOURCE is not set, so nothing looks for archived WAL. Point it at the ' +
        "directory PostgreSQL's archive_command writes to."
      );
    }

    if (!existsSync(source)) {
      return (
        `BACKUP_WAL_SOURCE points at "${source}", which does not exist. Either archive_command ` +
        'has never run, or it writes somewhere else.'
      );
    }

    if (!this.destination()) {
      return 'No R2 destination configured, so archived WAL has nowhere to go.';
    }

    // Segments present but none readable is a DIFFERENT state from "none yet",
    // and reporting it as the latter is how a stalled archive looks healthy.
    //
    // It is also the likely one: archive_command runs as postgres and WAL
    // segments in pg_wal are mode 0600, so a plain `cp` produces files this
    // process cannot open. Every upload then fails with EACCES, nothing is ever
    // pruned, and the directory grows by 16 MB a segment.
    try {
      const segments = readdirSync(source).filter((entry) => /^[0-9A-F]{24}$/.test(entry));
      const readable = segments.filter((entry) => {
        try {
          accessSync(join(source, entry), fsConstants.R_OK);
          return true;
        } catch {
          return false;
        }
      });

      if (segments.length > 0 && readable.length === 0) {
        return (
          `${segments.length} WAL segment(s) are in "${source}" but none can be read by this ` +
          `process (running as "${userInfo().username}"). archive_command must create them ` +
          `readable — use \`install -m 644\` rather than \`cp\`, which copies pg_wal's 0600 mode.`
        );
      }
    } catch {
      // A directory we cannot list is already covered by the checks above.
    }

    return null;
  }

  async collectWal(targetId: string, repositoryId: string | null): Promise<{ collected: number }> {
    const source = process.env.BACKUP_WAL_SOURCE;
    const destination = this.destination();
    if (!source || !existsSync(source) || !destination) return { collected: 0 };

    const entries = await readdir(source);
    // Exactly 24 hex characters is a WAL segment. Backup-history and partial
    // files carry suffixes and are excluded — they are not replayable.
    const segments = entries.filter((entry) => /^[0-9A-F]{24}$/.test(entry));

    let collected = 0;

    for (const segmentName of segments) {
      const timeline = parseInt(segmentName.slice(0, 8), 16) || 1;

      const existing = await this.prisma.walArchiveSegment.findUnique({
        where: { targetId_segmentName_timeline: { targetId, segmentName, timeline } },
      });
      if (existing?.status === 'uploaded') continue;

      const from = join(source, segmentName);

      try {
        const info = await stat(from);
        const raw = await readFile(from);

        // Gzipped before upload. Measured on this database: a busy segment goes
        // 16 MB -> 2.2 MB, and a mostly-empty one 16 MB -> 20 KB, averaging
        // 47:1. Against a 1 GB budget that is the difference between a WAL
        // window of hours and one of weeks. The extension stays as specified;
        // `sizeBytes` (raw) and `compressedBytes` record both halves, and the
        // restore path gunzips.
        const buffer = gzipSync(raw, { level: 9 });
        const key = `${walPrefix()}/${walFileName(segmentName)}`;

        await destination.save(buffer, key, 'application/octet-stream');

        await this.prisma.walArchiveSegment.upsert({
          where: { targetId_segmentName_timeline: { targetId, segmentName, timeline } },
          create: {
            targetId,
            repositoryId,
            segmentName,
            timeline,
            sizeBytes: info.size,
            compressedBytes: buffer.byteLength,
            checksum: createHash('sha256').update(raw).digest('hex'),
            status: 'uploaded',
            archivedAt: info.mtime,
            uploadedAt: new Date(),
            lagSeconds: Math.max(0, Math.round((Date.now() - info.mtime.getTime()) / 1000)),
          },
          update: {
            status: 'uploaded',
            uploadedAt: new Date(),
            compressedBytes: buffer.byteLength,
            repositoryId,
          },
        });

        collected++;
      } catch (err) {
        this.logger.warn(`could not upload WAL ${segmentName}: ${(err as Error).message}`);
      }
    }

    return { collected };
  }

  /**
   * Delete one object, refusing anything outside this environment's backup
   * prefix.
   *
   * The scope check is not paranoia about our own callers — it is about the
   * shared bucket. Media and backups live in the same R2 bucket separated only
   * by key prefix, so a delete that accepted an arbitrary key could remove a
   * merchant's product images. The prefix check makes that unreachable rather
   * than merely unlikely, and it also stops a `test` process from touching a
   * `production/` key.
   */
  async deleteObject(key: string): Promise<void> {
    const destination = this.destination();
    if (!destination) throw new Error(this.unavailableReason() ?? 'no backup destination');

    const allowed = `${backupRootPrefix()}/`;
    if (!key.startsWith(allowed)) {
      throw new Error(
        `Refusing to delete "${key}": outside this environment's backup prefix ("${allowed}"). ` +
          `Media and backups share one bucket, so an unscoped delete could remove merchant files.`
      );
    }

    await destination.delete(key);
  }

  /** psql, found the same way pg_dump is. */
  psqlPath(): string | null {
    if (this.resolved['psql'] === undefined) {
      this.resolved['psql'] = resolveBinary('psql', process.env.PSQL_PATH);
    }
    return this.resolved['psql'];
  }

  /** The database this process is actually connected to. Never a restore target. */
  liveDatabase(): string {
    try {
      return databaseUrl().pathname.replace(/^\//, '');
    } catch {
      return '';
    }
  }

  /**
   * Restore a dump into a SCRATCH database.
   *
   * ── WHY IT WILL NOT TOUCH THE LIVE DATABASE ─────────────────────────────────
   *
   * The dump carries `DROP ... IF EXISTS` before every `CREATE`, so pointed at
   * the live database it destroys the current data and replaces it with an
   * older copy — doing exactly what it was told, with no warning and no undo.
   * That is a decision for a human at a terminal with the runbook open, not a
   * web request. So this refuses the live name outright; there is no override
   * parameter, because a flag on an HTTP endpoint is not the ceremony that
   * decision deserves.
   *
   * The scratch database is created here rather than assumed, so the caller
   * never has to pre-provision one, and the name is validated as a bare
   * identifier before it reaches any SQL.
   */
  async restoreDump(
    storageKey: string,
    targetDatabase: string,
    overwriteLive = false
  ): Promise<{ durationMs: number; sizeBytes: number }> {
    const binary = this.psqlPath();
    if (!binary) throw new Error('psql not found. Set PSQL_PATH to its full path.');

    const destination = this.destination();
    if (!destination) throw new Error(this.unavailableReason() ?? 'no backup destination');

    // Identifier, not free text: it is interpolated into CREATE DATABASE, which
    // takes no parameters.
    if (!/^[a-z_][a-z0-9_]{0,62}$/.test(targetDatabase)) {
      throw new Error(
        `"${targetDatabase}" is not a valid database name (lowercase letters, digits and underscores).`
      );
    }

    // Restoring over the live database of THIS environment is allowed — that is
    // what "restore" means to an operator, and refusing outright just pushes
    // them to a terminal where nothing is recorded.
    //
    // What is not allowed is doing it silently. Two things stand in the way:
    // the caller must pass `overwriteLive`, and BackupService takes a fresh
    // pre-restore dump first, so the state being replaced is itself recoverable.
    // Overwriting production additionally needs a second approver.
    if (targetDatabase === this.liveDatabase() && !overwriteLive) {
      throw new Error(
        `"${targetDatabase}" is the live database for the "${backupEnvPrefix()}" environment. ` +
          `Restoring over it replaces current data with the state at backup time. Pass ` +
          `overwriteLive to proceed — a pre-restore backup is taken automatically first.`
      );
    }

    const url = databaseUrl();
    const password = url.password ? decodeURIComponent(url.password) : null;
    const base = [
      '--host', url.hostname,
      '--port', url.port || '5432',
      '--username', decodeURIComponent(url.username || 'postgres'),
    ];
    const env = { ...process.env, ...(password ? { PGPASSWORD: password } : {}) };

    const started = Date.now();
    const staging = join(tmpdir(), 'forgestack-restore');
    await mkdir(staging, { recursive: true });
    const stagedPath = join(staging, storageKey.split('/').pop() ?? 'restore.sql');

    const run = (args: string[], timeoutMs: number) =>
      new Promise<void>((resolvePromise, reject) => {
        execFile(binary, args, { env, shell: false, maxBuffer: 64 * 1024 * 1024, timeout: timeoutMs },
          (err, _stdout, stderr) => {
            if (err) reject(new Error(String(stderr || err.message).slice(0, 2000)));
            else resolvePromise();
          });
      });

    try {
      const stored = Buffer.from(await destination.read(storageKey));
      // Objects written before compression are plain .sql and must keep
      // restoring — reading an older archive is exactly what a restore is for.
      const buffer = storageKey.endsWith('.gz') ? gunzipSync(stored) : stored;
      await writeFile(stagedPath, buffer);

      // A scratch target is recreated so a re-run is repeatable rather than
      // layering a second restore onto the remains of the first. The LIVE
      // database is not dropped: DROP DATABASE fails while the API holds
      // connections, and the dump already carries DROP ... IF EXISTS per object,
      // which is the right granularity here.
      if (targetDatabase !== this.liveDatabase()) {
        await run([...base, '--dbname', 'postgres', '-c', `DROP DATABASE IF EXISTS ${targetDatabase}`], 120_000);
        await run([...base, '--dbname', 'postgres', '-c', `CREATE DATABASE ${targetDatabase}`], 120_000);
      }

      // ON_ERROR_STOP so a half-restored database cannot be mistaken for a
      // working one.
      await run(
        [...base, '--dbname', targetDatabase, '--variable', 'ON_ERROR_STOP=1', '--file', stagedPath],
        60 * 60 * 1000
      );

      return { durationMs: Date.now() - started, sizeBytes: buffer.byteLength };
    } finally {
      await rm(stagedPath, { force: true }).catch(() => undefined);
    }
  }

  /** `pg_ctl`, beside whichever pg_dump was found. */
  pgCtlPath(): string | null {
    const dump = this.pgDumpPath();
    if (!dump) return null;
    if (dump === 'pg_dump') return 'pg_ctl';
    const candidate = dump.replace(/pg_dump\.exe$/i, 'pg_ctl.exe');
    return existsSync(candidate) ? candidate : null;
  }

  /**
   * Start a recovered data directory as a second Postgres on its own port.
   *
   * `-w` waits for it to actually accept connections rather than returning as
   * soon as the process exists — during PITR the server spends time replaying
   * WAL before it is ready, and reporting success at fork time would have the
   * caller query a server that is still recovering.
   */
  async startSandbox(dataDir: string, port: number): Promise<void> {
    const binary = this.pgCtlPath();
    if (!binary) throw new Error('pg_ctl not found. Set PG_DUMP_PATH to a real PostgreSQL bin dir.');

    const logFile = `${dataDir}.log`;

    await new Promise<void>((resolvePromise, reject) => {
      execFile(
        binary,
        [
          '-D', toPosixPath(dataDir),
          '-o', `-p ${port}`,
          '-l', toPosixPath(logFile),
          '-w',
          // Replay can take a while on a long WAL chain; a short timeout here
          // reports failure for a recovery that was merely still working.
          '-t', '300',
          'start',
        ],
        { shell: false, timeout: 20 * 60 * 1000, maxBuffer: 16 * 1024 * 1024 },
        (err, _stdout, stderr) => {
          if (err) {
            reject(
              new Error(
                `pg_ctl start failed: ${String(stderr || err.message).slice(0, 600)}. ` +
                  `The recovery log is at ${logFile}.`
              )
            );
            return;
          }
          resolvePromise();
        }
      );
    });
  }

  /**
   * Stop a sandbox and delete its data directory.
   *
   * `-m immediate` because there is nothing to preserve — this cluster exists
   * only to be inspected, and a graceful checkpoint on a 435 MB throwaway is
   * time spent for no benefit.
   *
   * The directory is removed too: a forgotten sandbox is how a disk fills, and
   * on the VPS that means production Postgres stops accepting writes.
   */
  async stopSandbox(dataDir: string): Promise<void> {
    const binary = this.pgCtlPath();

    if (binary && existsSync(dataDir)) {
      await new Promise<void>((resolvePromise) => {
        execFile(
          binary,
          ['-D', toPosixPath(dataDir), '-m', 'immediate', '-w', '-t', '60', 'stop'],
          { shell: false, timeout: 5 * 60 * 1000 },
          // Resolve either way: a sandbox that is already down is not a failure,
          // and the directory removal below matters more than a clean stop.
          () => resolvePromise()
        );
      });
    }

    await rm(dataDir, { recursive: true, force: true }).catch(() => undefined);
    await rm(`${dataDir}.log`, { force: true }).catch(() => undefined);
  }

  /** Base backups available to restore from, newest first. */
  async listBaseBackups(): Promise<Array<{ prefix: string; timestamp: string }>> {
    const destination = this.destination();
    if (!destination) return [];

    const seen = new Set<string>();
    let cursor: string | undefined;

    do {
      const page = await destination.list(`${basePrefix()}/`, cursor);
      for (const key of page.keys) {
        // <env>/database/backups/base/<timestamp>/<file>
        const timestamp = key.slice(`${basePrefix()}/`.length).split('/')[0];
        if (timestamp) seen.add(timestamp);
      }
      cursor = page.nextCursor;
    } while (cursor);

    return [...seen]
      .sort()
      .reverse()
      .map((timestamp) => ({ prefix: baseBackupPrefix(timestamp), timestamp }));
  }

  /**
   * Restore a base backup and replay WAL to a chosen instant.
   *
   * This is real point-in-time recovery: the base backup is the starting
   * position, the WAL is every change since, and `recovery_target_time` says
   * where to stop. Proven end-to-end on this platform — restoring the same base
   * backup with two different targets yields two different databases, which is
   * the only test that actually demonstrates PITR rather than "a restore ran".
   *
   * ── THE TWO THINGS THAT SILENTLY BREAK THIS ─────────────────────────────────
   *
   * 1. Backslashes in `restore_command`. PostgreSQL treats a backslash as an
   *    escape inside a quoted value, so the file needs DOUBLED backslashes to
   *    yield single ones. Written with single ones, recovery finds no segments
   *    and waits forever. `String.raw` and the explicit doubling below exist for
   *    exactly this.
   * 2. A missing timezone on the target. Without an offset Postgres assumes the
   *    server's, and the restore lands hours from where it was asked to.
   */
  async restoreToPointInTime(options: {
    basePrefix: string;
    targetTime: Date;
    dataDir: string;
    port: number;
  }): Promise<{ walRestoreDir: string }> {
    const destination = this.destination();
    if (!destination) throw new Error(this.unavailableReason() ?? 'no backup destination');

    const tarBinary = process.env.TAR_PATH ?? 'tar';

    await rm(options.dataDir, { recursive: true, force: true }).catch(() => undefined);
    await mkdir(options.dataDir, { recursive: true });

    // 1. Base tarball: zstd from the server, decompressed natively by Node 22,
    //    then extracted with the tar that ships in Windows System32.
    const page = await destination.list(`${options.basePrefix}/`);
    const baseKey = page.keys.find((k) => k.endsWith('base.tar.zst'));
    if (!baseKey) throw new Error(`No base.tar.zst under ${options.basePrefix}`);

    const staging = join(tmpdir(), `forgestack-pitr-${options.port}`);
    await mkdir(staging, { recursive: true });
    const tarPath = join(staging, 'base.tar');

    await writeFile(tarPath, zstdDecompressSync(Buffer.from(await destination.read(baseKey))));
    await this.extractTar(tarBinary, tarPath, options.dataDir);

    // 2. The WAL streamed during the backup. Without it the base is not
    //    consistent and recovery cannot even begin.
    const walKey = page.keys.find((k) => k.endsWith('pg_wal.tar.gz'));
    if (walKey) {
      const walTar = join(staging, 'pg_wal.tar');
      await writeFile(walTar, gunzipSync(Buffer.from(await destination.read(walKey))));
      await this.extractTar(tarBinary, walTar, join(options.dataDir, 'pg_wal'));
    }

    // 3. Every archived segment, gunzipped into a local directory that
    //    restore_command reads from. Fetching them up front rather than on
    //    demand keeps restore_command a plain file copy with no network in it.
    const walRestoreDir = join(staging, 'wal');
    await mkdir(walRestoreDir, { recursive: true });

    let cursor: string | undefined;
    do {
      const walPage = await destination.list(`${walPrefix()}/`, cursor);
      for (const key of walPage.keys) {
        const segment = segmentNameFromFile(key.split('/').pop() ?? '');
        if (!/^[0-9A-F]{24}$/.test(segment)) continue;
        await writeFile(
          join(walRestoreDir, segment),
          gunzipSync(Buffer.from(await destination.read(key)))
        );
      }
      cursor = walPage.nextCursor;
    } while (cursor);

    // 4. Recovery configuration.
    //
    // PostgreSQL treats a backslash as an escape INSIDE a quoted value, so the
    // file must contain doubled backslashes to yield single ones in the setting.
    // Written with single ones, recovery finds no segments and waits forever —
    // verified the hard way; the working archive_command line has twice the
    // backslash count of a naively-written one.
    const BACKSLASH = String.fromCharCode(92);
    const winDir = walRestoreDir.split('/').join(BACKSLASH);
    const escaped = winDir.split(BACKSLASH).join(BACKSLASH + BACKSLASH);
    const sep = BACKSLASH + BACKSLASH;

    // The offset is explicit: without one Postgres assumes the server's
    // timezone and the restore lands hours from where it was asked to.
    const target = `${this.localTimestamp(options.targetTime)}${this.timezoneOffset(options.targetTime)}`;

    await writeFile(
      join(options.dataDir, 'postgresql.auto.conf'),
      [
        `restore_command = 'copy "${escaped}${sep}%f" "%p"'`,
        `recovery_target_time = '${target}'`,
        `recovery_target_action = 'promote'`,
        '',
      ].join('\n'),
      'utf8'
    );

    // The signal file is what makes Postgres recover rather than start normally.
    await writeFile(join(options.dataDir, 'recovery.signal'), '', 'utf8');

    await rm(tarPath, { force: true }).catch(() => undefined);

    return { walRestoreDir };
  }

  private extractTar(binary: string, tarPath: string, into: string): Promise<void> {
    return new Promise((resolvePromise, reject) => {
      execFile(
        binary,
        // tar (bsdtar, in Windows System32) wants forward slashes.
        ['-xf', toPosixPath(tarPath), '-C', toPosixPath(into)],
        { shell: false, timeout: 30 * 60 * 1000, maxBuffer: 32 * 1024 * 1024 },
        (err, _stdout, stderr) => {
          if (err) reject(new Error(`tar failed: ${String(stderr || err.message).slice(0, 500)}`));
          else resolvePromise();
        }
      );
    });
  }

  /** `2026-08-29 21:08:40.652676` — no timezone; the offset is appended separately. */
  private localTimestamp(at: Date): string {
    const pad = (n: number, w = 2) => String(n).padStart(w, '0');
    return (
      `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ` +
      `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}.${pad(at.getMilliseconds(), 3)}000`
    );
  }

  /** `+05:30`. Written explicitly because omitting it lands the restore hours away. */
  private timezoneOffset(at: Date): string {
    const minutes = -at.getTimezoneOffset();
    const sign = minutes >= 0 ? '+' : '-';
    const abs = Math.abs(minutes);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  }

  /**
   * Delete local segments that are confirmed present in R2.
   *
   * ── WHY THERE IS A LOCAL COPY AT ALL ────────────────────────────────────────
   *
   * Postgres cannot push to object storage. `archive_command` runs a shell
   * command against a file path, so a segment must land on disk before anything
   * can ship it. That local file is a HANDOFF POINT, not a backup — and the
   * handoff is deliberate rather than lazy: if `archive_command` itself tried to
   * upload and the network was down, it would fail, Postgres would refuse to
   * recycle WAL, `pg_wal` would grow until the disk filled, and the database
   * would stop accepting writes. Copying locally and shipping asynchronously is
   * exactly the pattern pgBackRest uses for the same reason.
   *
   * What is NOT deliberate is leaving it there afterwards. Nothing was deleting
   * these, so the archive directory grew without bound — 177 MB before this
   * existed — which is both "a backup stored locally" and the disk-fill risk
   * this whole subsystem is meant to prevent.
   *
   * ── VERIFY BEFORE DELETING ──────────────────────────────────────────────────
   *
   * A local file is removed only after the object is confirmed in R2 AT THE
   * RIGHT SIZE. Trusting our own database row would be a mistake: the row says
   * what we believe, and the point of the check is to catch the case where that
   * belief is wrong. Deleting the only copy of a WAL segment on the strength of
   * a stale row is how a recovery window acquires a hole.
   */
  async pruneUploadedWal(targetId: string): Promise<{ removed: number; freedBytes: bigint }> {
    const source = process.env.BACKUP_WAL_SOURCE;
    const destination = this.destination();
    if (!source || !existsSync(source) || !destination) return { removed: 0, freedBytes: 0n };

    const entries = await readdir(source);
    const segments = entries.filter((entry) => /^[0-9A-F]{24}$/.test(entry));

    let removed = 0;
    let freedBytes = 0n;

    for (const segmentName of segments) {
      const timeline = parseInt(segmentName.slice(0, 8), 16) || 1;

      const row = await this.prisma.walArchiveSegment.findUnique({
        where: { targetId_segmentName_timeline: { targetId, segmentName, timeline } },
      });
      if (row?.status !== 'uploaded') continue;

      const key = `${walPrefix()}/${walFileName(segmentName)}`;

      try {
        const meta = await destination.getMetadata(key);
        // Size must match what we recorded storing. A truncated upload that
        // still "exists" is the case worth catching.
        if (row.compressedBytes > 0 && meta.sizeBytes !== row.compressedBytes) {
          this.logger.warn(
            `keeping local ${segmentName}: R2 object is ${meta.sizeBytes} bytes, expected ${row.compressedBytes}`
          );
          continue;
        }
      } catch {
        // Not readable in R2 — the local copy is the only one. Keep it.
        this.logger.warn(`keeping local ${segmentName}: not confirmed in R2`);
        continue;
      }

      const local = join(source, segmentName);
      try {
        freedBytes += BigInt((await stat(local)).size);
        await rm(local, { force: true });
        removed++;
      } catch (err) {
        // ENOENT means the file is already gone — archive_command churn, or a
        // sweep that overlapped this one. The goal is "not present locally",
        // and it is met, so this is not worth a warning: logging it trains
        // people to ignore the ones that matter.
        if ((err as NodeJS.ErrnoException).code === 'ENOENT') continue;
        this.logger.warn(`could not remove local ${segmentName}: ${(err as Error).message}`);
      }
    }

    if (removed) {
      this.logger.log(
        `removed ${removed} local WAL segment(s) already in R2, freeing ${(Number(freedBytes) / 1048576).toFixed(0)} MB`
      );
    }

    return { removed, freedBytes };
  }

  /** Delete one WAL object by segment name. Scoped by deleteObject. */
  async deleteWalObject(segmentName: string): Promise<void> {
    await this.deleteObject(`${walPrefix()}/${walFileName(segmentName)}`);
  }

  /**
   * Delete an entire base backup directory.
   *
   * All of its parts or none: the tarball is useless without the streamed WAL
   * that makes it consistent, so a partial delete leaves an object that looks
   * restorable and is not.
   */
  async deleteBaseBackup(prefix: string): Promise<void> {
    const destination = this.destination();
    if (!destination) throw new Error(this.unavailableReason() ?? 'no backup destination');

    const keys: string[] = [];
    let cursor: string | undefined;
    do {
      const page = await destination.list(`${prefix}/`, cursor);
      keys.push(...page.keys);
      cursor = page.nextCursor;
    } while (cursor);

    // Routed through deleteObject one by one so every key gets the same
    // prefix check — a bulk delete would skip the guard that stops this
    // touching media in the shared bucket.
    for (const key of keys) await this.deleteObject(key);
  }

  /** Strip whatever extension the WAL store uses, to get the segment name back. */
  segmentNameOf(filename: string): string {
    return segmentNameFromFile(filename);
  }

  /**
   * Bytes held under this environment's prefix, counted from the bucket.
   *
   * Listed rather than accumulated: a running total that drifts is worse than
   * no total, because it is trusted. This is the same reasoning the media
   * storage quota already applies.
   */
  async usage(): Promise<{
    dumpBytes: bigint;
    baseBytes: bigint;
    walBytes: bigint;
    objectCount: number;
  }> {
    const destination = this.destination();
    if (!destination) return { dumpBytes: 0n, baseBytes: 0n, walBytes: 0n, objectCount: 0 };

    const measure = async (prefix: string) => {
      let bytes = 0n;
      let count = 0;
      let cursor: string | undefined;

      do {
        const page = await destination.list(prefix, cursor);
        for (const key of page.keys) {
          try {
            const meta = await destination.getMetadata(key);
            bytes += BigInt(meta.sizeBytes);
            count++;
          } catch {
            // An object removed between listing and stat is not worth failing
            // a usage report over.
          }
        }
        cursor = page.nextCursor;
      } while (cursor);

      return { bytes, count };
    };

    const dumps = await measure(`${dumpPrefix()}/`);
    // Base backups were missing from this sum, so they were invisible to the
    // quota — 43 MB of a 1 GB budget unaccounted for, and growing with every
    // PITR backup. A quota that does not count everything is a quota that
    // eventually lets the bucket fill.
    const base = await measure(`${basePrefix()}/`);
    const wal = await measure(`${walPrefix()}/`);

    return {
      dumpBytes: dumps.bytes,
      baseBytes: base.bytes,
      walBytes: wal.bytes,
      objectCount: dumps.count + base.count + wal.count,
    };
  }

  /**
   * Delete base backups beyond the retention count, newest kept.
   *
   * Deletes the whole per-backup directory, because a base backup is only
   * restorable with all of its parts: the tarball alone is useless without the
   * streamed WAL that makes it consistent.
   *
   * ── WHY THE OLDEST BASE BACKUP IS NOT SAFE TO DROP CASUALLY ─────────────────
   *
   * A base backup is the earliest point its WAL can replay onto. Deleting the
   * oldest one moves the START of the recovery window forward, so anything
   * before the next surviving backup stops being recoverable — even though the
   * WAL for it is still sitting there. Retention here is therefore a decision
   * about how far back PITR reaches, not just about disk.
   */
  async pruneBaseBackups(keep: number): Promise<{ removed: number; freedBytes: bigint }> {
    const destination = this.destination();
    if (!destination) return { removed: 0, freedBytes: 0n };

    const backups = await this.listBaseBackups();
    const doomed = backups.slice(Math.max(keep, 1));
    if (!doomed.length) return { removed: 0, freedBytes: 0n };

    let freedBytes = 0n;
    for (const backup of doomed) {
      const keys: string[] = [];
      let cursor: string | undefined;

      do {
        const page = await destination.list(`${backup.prefix}/`, cursor);
        keys.push(...page.keys);
        cursor = page.nextCursor;
      } while (cursor);

      for (const key of keys) {
        try {
          freedBytes += BigInt((await destination.getMetadata(key)).sizeBytes);
        } catch {
          // Size unknown; the delete matters more than the accounting.
        }
      }

      if (keys.length) await destination.deleteMany(keys);
      this.logger.log(`pruned base backup ${backup.timestamp} (${keys.length} object(s))`);
    }

    return { removed: doomed.length, freedBytes };
  }

  /**
   * Delete dumps beyond the retention count.
   *
   * Newest-first by key, which is why the timestamp format is
   * lexicographically sortable. Scoped to this environment's dump prefix, so a
   * `test` process can never expire a `production` object — the mistake the
   * boot-time environment assertion exists to make impossible in the first
   * place, guarded again here because deletion deserves two locks.
   */
  async prune(keep: number): Promise<{ removed: number; freedBytes: bigint }> {
    const destination = this.destination();
    if (!destination) return { removed: 0, freedBytes: 0n };

    const prefix = `${dumpPrefix()}/`;
    const keys: string[] = [];
    let cursor: string | undefined;

    do {
      const page = await destination.list(prefix, cursor);
      keys.push(...page.keys.filter((key) => key.endsWith('.sql') || key.endsWith('.sql.gz')));
      cursor = page.nextCursor;
    } while (cursor);

    const doomed = keys.sort().reverse().slice(Math.max(keep, 1));
    if (!doomed.length) return { removed: 0, freedBytes: 0n };

    let freedBytes = 0n;
    for (const key of doomed) {
      try {
        freedBytes += BigInt((await destination.getMetadata(key)).sizeBytes);
      } catch {
        // Size unknown; the delete below still matters more than the accounting.
      }
    }

    await destination.deleteMany(doomed);
    return { removed: doomed.length, freedBytes };
  }
}
